import { Text } from "@/components/ui/baseUi/text"
import { FileUploadImage } from "@/modules/ui/components/fileUpload/fileUploadImage"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useProfileAvatarFormik } from "@modules/profile/myProfile/hooks/useProfileAvatarFormik"
import { type HUImage } from "@modules/types/imageTypes"
import { Circles } from "@modules/ui/components/circles"
import { UserProfileEntity } from "@/modules/users/entities/usersEntity"
import { CameraIcon, PenIcon } from "lucide-react"
import { useEffect, useMemo } from "react"

type ProfileAvatarFormProps = {
  user: UserProfileEntity
}

export function ProfileAvatarForm({ user }: ProfileAvatarFormProps) {
  const { formik, isSubmitting } = useProfileAvatarFormik(user)

  useEffect(() => {
    if (formik.values.avatar?.key && formik.initialValues.avatar?.key !== formik.values.avatar.key) formik.submitForm()
  }, [formik.values.avatar?.key])

  const completion = UserProfileEntity.getProfileCompletion(user)
  const isOnlyMissingAvatar = UserProfileEntity.isOnlyMissingAvatar(user)

  const avatarImage: HUImage | undefined = useMemo(() => {
    const url = formik.values.avatar?.file
      ? URL.createObjectURL(formik.values.avatar.file)
      : (formik.values.avatar?.url ?? undefined)
    return url ? { url, hash: "", key: formik.values.avatar?.key } : undefined
  }, [formik.values.avatar])

  return (
    <div className="card mb-0 flex flex-col items-center bg-neutral-100">
      {/* Profile avatar input */}
      <div className="flex w-full flex-row items-center justify-center gap-4">
        <FileUploadImage
          setImage={(e) => {
            formik.setFieldValue("avatar", e)
          }}
          disabled={isSubmitting}
          className="w-fit"
        >
          <div className="flex flex-row justify-center">
            <div className="relative my-4 h-fit w-fit">
              <ProfileAvatar
                profile={{ avatar: avatarImage ?? null, username: user.username ?? "" }}
                className="size-25 **:text-5xl"
              />
              <div className="absolute right-1 bottom-1 rounded-full bg-gray-900 p-2 ring-3 ring-neutral-50">
                {formik.values.avatar ? (
                  <PenIcon className="size-4 text-textOnColor-body" />
                ) : (
                  <CameraIcon className="size-4 text-textOnColor-body" />
                )}
              </div>
            </div>
          </div>
        </FileUploadImage>

        <div className="flex max-w-[60%] flex-col items-start overflow-hidden">
          <Text fontStyle="HS" className="line-clamp-2 inline-block w-full overflow-hidden text-ellipsis">
            {`${user.firstname} ${user.lastname}`}
          </Text>
          <Text fontStyle="BM">{`@${user.username}`}</Text>
        </div>
      </div>

      {/* Profile completion ratio */}
      {completion.pcRatio !== 1 &&
        (isOnlyMissingAvatar ? (
          <div className="flex flex-row items-center justify-center gap-2">
            <WarningHexagonalIcon color="currentColor" className="text-textError" size={24} />
            <Text fontStyle="LS">{word("profile.form.avatar_missing")}</Text>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Circles count={10} filled={completion.pcRatio * 10} color="currentColor" progressionstyle="line" />
            <Text fontStyle="LS" className="text-center">
              {word("profile.form.completion", { percentage: Math.ceil(completion.pcRatio * 100) })}
            </Text>
          </div>
        ))}
    </div>
  )
}
